import React, { useState, useEffect } from 'react';
import { X, Package, FileText, Clock, Wrench, CheckSquare, BookOpen, AlertTriangle, Info } from 'lucide-react';
import { Button } from '../../../catalyst/button';
import { Input } from '../../../catalyst/input';
import { Textarea } from '../../../catalyst/textarea';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { FFE_GUIDANCE } from '../../../../config/guidance/ffe.guidance';
import { useStorageState } from '../../../../hooks/useStorageState';
import { useCustomDialogs } from '../../../../hooks/useCustomDialogs';
import { getJobDisplayName } from '../../../../utils/maintenanceJobHelpers';
// FT-189: Centralized equipment name resolution
import { getEquipmentDisplayName } from '../../../../services/equipment/utils/equipmentRow';

const EquipmentDetailModal = ({ isOpen, onClose, equipment, onUpdate }) => {
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({});
  const { value: maintenanceJobs = [] } = useStorageState('maintenanceRequirements', []);
  const { showAlert } = useCustomDialogs();
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (equipment) {
      setEditedData({
        // FT-189: Use canonical name field priority
        label: getEquipmentDisplayName(equipment),
        location: equipment.location || '',
        serialNumber: equipment.serialNumber || equipment.serialNo || '',
        lastServiceAt: equipment.lastServiceAt || '',
        notes: equipment.notes || '',
        specifications: equipment.specifications || ''
      });
    }
  }, [equipment]);

  if (!isOpen || !equipment) return null;

  // Find guidance for this equipment
  const guidance = equipment.guidanceKey && FFE_GUIDANCE.items[equipment.guidanceKey];
  
  // Find linked maintenance jobs
  const linkedJobs = maintenanceJobs.filter(job => {
    if (!equipment.linkedJobIds) return false;
    return equipment.linkedJobIds.includes(job.id);
  });

  const handleSave = () => {
    if (onUpdate) {
      onUpdate(equipment.id, editedData);
      showAlert('Equipment details updated');
      setEditMode(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative w-full max-w-3xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Package className="h-5 w-5 text-gray-400 mr-3" />
                <h2 className="text-lg font-medium text-gray-900">
                  {/* FT-189: Use centralized name helper */}
                  {getEquipmentDisplayName(equipment)}
                </h2>
                {equipment.guidanceKey && (
                  <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    <BookOpen className="h-3 w-3 mr-1" />
                    Guidance Available
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <TabGroup selectedIndex={selectedTab} onChange={setSelectedTab}>
              <TabList className="flex space-x-1 rounded-xl bg-gray-100 p-1">
                <Tab className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5 ${
                    selected
                      ? 'bg-white text-blue-700 shadow'
                      : 'text-gray-700 hover:bg-white/[0.12] hover:text-gray-900'
                  }`
                }>
                  Details
                </Tab>
                {guidance && (
                  <Tab className={({ selected }) =>
                    `w-full rounded-lg py-2.5 text-sm font-medium leading-5 ${
                      selected
                        ? 'bg-white text-blue-700 shadow'
                        : 'text-gray-700 hover:bg-white/[0.12] hover:text-gray-900'
                    }`
                  }>
                    Guidance
                  </Tab>
                )}
                <Tab className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5 ${
                    selected
                      ? 'bg-white text-blue-700 shadow'
                      : 'text-gray-700 hover:bg-white/[0.12] hover:text-gray-900'
                  }`
                }>
                  History
                </Tab>
                <Tab className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5 ${
                    selected
                      ? 'bg-white text-blue-700 shadow'
                      : 'text-gray-700 hover:bg-white/[0.12] hover:text-gray-900'
                  }`
                }>
                  Documents
                </Tab>
              </TabList>

              <TabPanels className="mt-4">
                {/* Details Tab */}
                <TabPanel>
                  {editMode ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Equipment Name
                        </label>
                        <Input
                          value={editedData.label}
                          onChange={(e) => setEditedData({...editedData, label: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location
                        </label>
                        <Input
                          value={editedData.location}
                          onChange={(e) => setEditedData({...editedData, location: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Serial Number
                        </label>
                        <Input
                          value={editedData.serialNumber}
                          onChange={(e) => setEditedData({...editedData, serialNumber: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Service Date
                        </label>
                        <Input
                          type="date"
                          value={editedData.lastServiceAt}
                          onChange={(e) => setEditedData({...editedData, lastServiceAt: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Specifications
                        </label>
                        <Textarea
                          value={editedData.specifications}
                          onChange={(e) => setEditedData({...editedData, specifications: e.target.value})}
                          rows={3}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notes
                        </label>
                        <Textarea
                          value={editedData.notes}
                          onChange={(e) => setEditedData({...editedData, notes: e.target.value})}
                          rows={3}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Category</span>
                          <p className="mt-1 text-sm text-gray-900">{equipment.category || 'Other'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Source</span>
                          <p className="mt-1 text-sm text-gray-900">
                            {(equipment.source === 'requirements' || equipment.sourceRequirementId) ? 'Required' : 'Added'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Location</span>
                          <p className="mt-1 text-sm text-gray-900">{equipment.location || 'Not specified'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Serial Number</span>
                          <p className="mt-1 text-sm text-gray-900">
                            {equipment.serialNumber || equipment.serialNo || 'Not specified'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Last Service</span>
                          <p className="mt-1 text-sm text-gray-900">{formatDate(equipment.lastServiceAt)}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Added</span>
                          <p className="mt-1 text-sm text-gray-900">{formatDate(equipment.createdAt)}</p>
                        </div>
                      </div>
                      
                      {equipment.specifications && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Specifications</span>
                          <p className="mt-1 text-sm text-gray-900">{equipment.specifications}</p>
                        </div>
                      )}
                      
                      {equipment.notes && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Notes</span>
                          <p className="mt-1 text-sm text-gray-900">{equipment.notes}</p>
                        </div>
                      )}
                      
                      {linkedJobs.length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Linked Maintenance</span>
                          <div className="mt-2 space-y-2">
                            {linkedJobs.map(job => (
                              <div key={job.id} className="flex items-center p-2 bg-gray-50 rounded">
                                <Wrench className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-900">{getJobDisplayName(job)}</span>
                                <span className="ml-auto text-xs text-gray-500">{job.interval}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </TabPanel>

                {/* Guidance Tab */}
                {guidance && (
                  <TabPanel>
                    <div className="space-y-6">
                      {/* Placement */}
                      {guidance.placement && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Placement Requirements</h4>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-900">{guidance.placement.primary}</p>
                            {guidance.placement.additional && (
                              <p className="mt-1 text-sm text-blue-700">{guidance.placement.additional}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Specifications */}
                      {guidance.specifications && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Specifications</h4>
                          <div className="space-y-1">
                            {Object.entries(guidance.specifications).map(([key, value]) => (
                              <div key={key} className="flex items-start">
                                <span className="text-sm font-medium text-gray-500 capitalize w-24">
                                  {key.replace(/_/g, ' ')}:
                                </span>
                                <span className="text-sm text-gray-900">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Checklists */}
                      {guidance.checklist && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Inspection Checklists</h4>
                          
                          {guidance.checklist.monthly && (
                            <div className="mb-4">
                              <h5 className="text-xs font-medium text-gray-700 uppercase tracking-wider mb-2">
                                Monthly Checks
                              </h5>
                              <div className="space-y-1">
                                {guidance.checklist.monthly.map((item, idx) => (
                                  <label key={idx} className="flex items-start">
                                    <CheckSquare className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                                    <span className="text-sm text-gray-700">{item}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {guidance.checklist.annual && (
                            <div>
                              <h5 className="text-xs font-medium text-gray-700 uppercase tracking-wider mb-2">
                                Annual Checks
                              </h5>
                              <div className="space-y-1">
                                {guidance.checklist.annual.map((item, idx) => (
                                  <label key={idx} className="flex items-start">
                                    <CheckSquare className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                                    <span className="text-sm text-gray-700">{item}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Tips */}
                      {guidance.tips && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Best Practices</h4>
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <ul className="space-y-1">
                              {guidance.tips.map((tip, idx) => (
                                <li key={idx} className="flex items-start">
                                  <Info className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                                  <span className="text-sm text-green-900">{tip}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </TabPanel>
                )}

                {/* History Tab */}
                <TabPanel>
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm">Service history will be displayed here</p>
                    <p className="text-xs text-gray-400 mt-1">Track maintenance and inspection records</p>
                  </div>
                </TabPanel>

                {/* Documents Tab */}
                <TabPanel>
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm">No documents attached</p>
                    <p className="text-xs text-gray-400 mt-1">Upload certificates, manuals, and service records</p>
                  </div>
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-between">
              <div>
                {!editMode && selectedTab === 0 && (
                  <Button 
                    variant="secondary" 
                    onClick={() => setEditMode(true)}
                  >
                    Edit Details
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                {editMode ? (
                  <>
                    <Button 
                      variant="secondary" 
                      onClick={() => {
                        setEditMode(false);
                        setEditedData({
                          // FT-189: Use centralized name helper
                          label: getEquipmentDisplayName(equipment),
                          location: equipment.location || '',
                          serialNumber: equipment.serialNumber || equipment.serialNo || '',
                          lastServiceAt: equipment.lastServiceAt || '',
                          notes: equipment.notes || '',
                          specifications: equipment.specifications || ''
                        });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={handleSave}
                    >
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <Button 
                    variant="secondary" 
                    onClick={onClose}
                  >
                    Close
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentDetailModal;